import React, { useEffect, useState } from 'react';

import Field from './Field';
import ArrayField from './ArrayField';
import ImageInputList from './ImageInputList';
import SingleImageInput from './SingleImageInput';
import ToggleCard from './ToggleCard';
import SummaryItem from './SummaryItem';
import copy from './i18n/sv.json';
import './App.css';

const strings = copy.createSiteAdmin;
const industries = strings.options.industries;
const toneOptions = strings.options.toneOptions;
const ctaOptions = strings.options.ctaOptions;
const themeOptions = strings.options.themeOptions;

const backendBaseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const isDebugMode = process.env.REACT_APP_DEBUG === 'true';
const FORM_STORAGE_KEY = 'create-site-admin-form';
const STEP_STORAGE_KEY = 'create-site-admin-step';
const POLL_INTERVAL_MS = 5000;
const WEEKDAYS = [
    'Måndag',
    'Tisdag',
    'Onsdag',
    'Torsdag',
    'Fredag',
    'Lördag',
    'Söndag'
];

function createEmptyImage() {
    return {
        type: 'url',
        value: ''
    };
}

function createOpeningHoursDefaults() {
    return WEEKDAYS.map((day) => ({
        day,
        opens: '',
        closes: '',
        closed: false
    }));
}

function normalizeOpeningHours(days) {
    const sourceDays = Array.isArray(days) ? days : [];

    return WEEKDAYS.map((day, index) => {
        const match = sourceDays.find((item) => item?.day === day) || sourceDays[index] || {};

        return {
            day,
            opens: String(match.opens || ''),
            closes: String(match.closes || ''),
            closed: Boolean(match.closed)
        };
    });
}

const defaultFormValues = strings.defaults.defaultFormValues;

const emptyFormValues = strings.defaults.emptyFormValues;

function buildSocialLinksArray(socialLinks) {
    return Object.entries(socialLinks)
        .filter(([, url]) => Boolean(url && url.trim()))
        .map(([platform, url]) => ({
            platform,
            url: url.trim()
        }));
}

function buildValidationMessage(result) {
    if (!Array.isArray(result?.details) || result.details.length === 0) {
        return result?.error || strings.messages.validationFailed;
    }

    return result.details
        .map((detail) => `${detail.path || 'root'}: ${detail.message}`)
        .join('\n');
}

function normalizeWebsiteUrl(value) {
    const trimmed = String(value || '').trim();

    if (!trimmed) {
        return '';
    }

    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }

    return `https://${trimmed}`;
}

function isEmptyValue(value) {
    if (Array.isArray(value)) {
        return value.filter(Boolean).length === 0;
    }

    if (value && typeof value === 'object') {
        if (Object.prototype.hasOwnProperty.call(value, 'value')) {
            return !String(value.value || '').trim();
        }

        return Object.values(value).every(isEmptyValue);
    }

    return !String(value || '').trim();
}

function getPathValue(source, path) {
    return path.split('.').reduce((current, key) => (current ? current[key] : undefined), source);
}

function setPathValue(source, path, value) {
    const keys = path.split('.');
    const next = { ...source };
    let current = next;

    for (let index = 0; index < keys.length - 1; index += 1) {
        const key = keys[index];
        current[key] = {
            ...(current[key] || {})
        };
        current = current[key];
    }

    current[keys[keys.length - 1]] = value;
    return next;
}

function formatAiSuggestion(value) {
    if (Array.isArray(value)) {
        return value.filter(Boolean).join(', ');
    }

    if (value && typeof value === 'object') {
        if (Object.prototype.hasOwnProperty.call(value, 'value')) {
            return value.value || '';
        }

        return Object.values(value).filter(Boolean).join(', ');
    }

    return String(value || '');
}

function isUsableAiValue(value) {
    if (Array.isArray(value)) {
        return value.some((item) => String(item || '').trim());
    }

    if (value && typeof value === 'object') {
        if (Object.prototype.hasOwnProperty.call(value, 'value')) {
            return Boolean(value.value && /^https?:\/\//i.test(value.value));
        }

        return Object.values(value).some(isUsableAiValue);
    }

    return Boolean(String(value || '').trim());
}

function createBaseFormValues() {
    return {
        currentWebsite: '',
        ...JSON.parse(JSON.stringify(isDebugMode ? defaultFormValues : emptyFormValues)),
        openingHours: normalizeOpeningHours((isDebugMode ? defaultFormValues : emptyFormValues).openingHours)
    };
}

function readStoredForm() {
    if (typeof window === 'undefined') {
        return createBaseFormValues();
    }

    try {
        const raw = window.localStorage.getItem(FORM_STORAGE_KEY);
        if (!raw) {
            return createBaseFormValues();
        }

        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') {
            return createBaseFormValues();
        }

        return {
            ...createBaseFormValues(),
            ...parsed,
            logo: {
                ...createBaseFormValues().logo,
                ...(parsed.logo || {})
            },
            socialLinks: {
                ...createBaseFormValues().socialLinks,
                ...(parsed.socialLinks || {})
            },
            services: Array.isArray(parsed.services) && parsed.services.length > 0 ? parsed.services : createBaseFormValues().services,
            usp: Array.isArray(parsed.usp) && parsed.usp.length > 0 ? parsed.usp : createBaseFormValues().usp,
            certifications: Array.isArray(parsed.certifications) && parsed.certifications.length > 0 ? parsed.certifications : createBaseFormValues().certifications,
            openingHours: normalizeOpeningHours(parsed.openingHours),
            images: Array.isArray(parsed.images) ? parsed.images : []
        };
    } catch (error) {
        window.localStorage.removeItem(FORM_STORAGE_KEY);
        return createBaseFormValues();
    }
}

function readStoredStep(totalSteps) {
    if (typeof window === 'undefined') {
        return 1;
    }

    const raw = window.localStorage.getItem(STEP_STORAGE_KEY);
    const parsed = Number.parseInt(raw || '1', 10);
    if (!Number.isFinite(parsed)) {
        return 1;
    }

    return Math.min(Math.max(parsed, 1), totalSteps);
}

function readStepFromUrl(totalSteps) {
    if (typeof window === 'undefined') {
        return null;
    }

    const params = new URLSearchParams(window.location.search);
    const raw = params.get('step');
    const parsed = Number.parseInt(raw || '', 10);

    if (!Number.isFinite(parsed)) {
        return null;
    }

    return Math.min(Math.max(parsed, 1), totalSteps);
}

function syncStepToUrl(step) {
    if (typeof window === 'undefined') {
        return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('step', String(step));
    window.history.replaceState({}, '', url.toString());
}

function formatWorkflowTime(value) {
    if (!value) {
        return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return new Intl.DateTimeFormat('sv-SE', { timeStyle: 'short' }).format(date);
}

function buildFallbackPagesUrl(repo) {
    const owner = repo?.owner;
    const name = repo?.name;

    if (!owner || !name) {
        return '';
    }

    if (name.toLowerCase() === `${owner.toLowerCase()}.github.io`) {
        return `https://${owner}.github.io/`;
    }

    return `https://${owner}.github.io/${name}/`;
}

function findJobByKeyword(run, keyword) {
    return (run?.jobs || []).find((job) => job.name && job.name.toLowerCase().includes(keyword));
}

function getWorkflowStep(job, baseLabel) {
    if (!job) {
        return { tone: 'waiting', label: baseLabel + ' ' + strings.workflow.waiting, time: null };

    }

    if (job.status !== 'completed') {
        return { tone: 'active', label: baseLabel + ' ' + strings.workflow.started, time: formatWorkflowTime(job.startedAt || job.updatedAt) };
    }

    if (job.conclusion === 'success') {
        return { tone: 'success', label: baseLabel + ' ' + strings.workflow.completed, time: formatWorkflowTime(job.completedAt || job.updatedAt) };
    }

    return { tone: 'error', label: baseLabel + ' ' + strings.workflow.failed, time: formatWorkflowTime(job.completedAt || job.updatedAt) };
}

function getRunDerivedStep(run, baseLabel) {
    if (!run) {
        return { tone: 'waiting', label: baseLabel + ' ' + strings.workflow.waiting, time: null };
    }

    if (run.status !== 'completed') {
        return { tone: 'active', label: baseLabel + ' ' + strings.workflow.started, time: formatWorkflowTime(run.createdAt || run.updatedAt) };
    }

    if (run.conclusion === 'success') {
        return { tone: 'success', label: baseLabel + ' ' + strings.workflow.completed, time: formatWorkflowTime(run.updatedAt) };
    }

    if (run.conclusion) {
        return { tone: 'error', label: baseLabel + ' ' + strings.workflow.failed, time: formatWorkflowTime(run.updatedAt) };
    }

    return { tone: 'waiting', label: baseLabel + ' ' + strings.workflow.waiting, time: formatWorkflowTime(run.updatedAt) };
}

export default function CreateSiteAdmin({ accessToken }) {
    const [step, setStep] = useState(() => readStepFromUrl(5) ?? readStoredStep(5));
    const [submitting, setSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [createdRepo, setCreatedRepo] = useState(null);
    const [pagesStatus, setPagesStatus] = useState('');
    const [pagesUrl, setPagesUrl] = useState('');
    const [repoConflict, setRepoConflict] = useState(null);
    const [form, setForm] = useState(() => readStoredForm());
    const [aiResearch, setAiResearch] = useState({
        loading: false,
        message: '',
        summary: '',
        sources: []
    });
    const [aiGeneratedFields, setAiGeneratedFields] = useState({});
    const [aiSuggestions, setAiSuggestions] = useState({});
    const [workflowTarget, setWorkflowTarget] = useState({ siteId: '', commitSha: '' });
    const [workflowState, setWorkflowState] = useState({ loading: false, error: '', buildStep: null, deployStep: null });
    const [pendingPagesUrl, setPendingPagesUrl] = useState('');

    const totalSteps = 5;

      const sectionTitle = strings.stepTitles;

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        window.localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(form));
    }, [form]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        window.localStorage.setItem(STEP_STORAGE_KEY, String(step));
        syncStepToUrl(step);
    }, [step]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        function handlePopState() {
            const nextStep = readStepFromUrl(totalSteps);
            if (nextStep) {
                setStep(nextStep);
            }
        }

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [totalSteps]);

    function clearAiStateForPath(path) {
        setAiGeneratedFields((prev) => {
            if (!prev[path]) {
                return prev;
            }

            const next = { ...prev };
            delete next[path];
            return next;
        });
        setAiSuggestions((prev) => {
            if (!prev[path]) {
                return prev;
            }

            const next = { ...prev };
            delete next[path];
            return next;
        });
    }

    function updateField(field, value) {
        clearAiStateForPath(field);
        setForm((prev) => ({ ...prev, [field]: value }));
    }

    function updateNestedField(group, field, value) {
        clearAiStateForPath(`${group}.${field}`);
        setForm((prev) => ({
            ...prev,
            [group]: {
                ...prev[group],
                [field]: value
            }
        }));
    }

    function updateArrayItem(field, index, value) {
        clearAiStateForPath(field);
        setForm((prev) => ({
            ...prev,
            [field]: prev[field].map((item, i) => (i === index ? value : item))
        }));
    }

    function addArrayItem(field) {
        clearAiStateForPath(field);
        setForm((prev) => ({
            ...prev,
            [field]: [...prev[field], '']
        }));
    }

    function removeArrayItem(field, index) {
        clearAiStateForPath(field);
        setForm((prev) => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index)
        }));
    }

    function updateOpeningHour(index, field, value) {
        setForm((prev) => ({
            ...prev,
            openingHours: normalizeOpeningHours(prev.openingHours).map((item, itemIndex) =>
                itemIndex === index
                    ? {
                        ...item,
                        [field]: field === 'closed' ? Boolean(value) : value
                    }
                    : item
            )
        }));
    }

    function addImage() {
        clearAiStateForPath('images');
        setForm((prev) => ({
            ...prev,
            images: [...prev.images, createEmptyImage()]
        }));
    }

    function updateLogoType(nextType) {
        clearAiStateForPath('logo');
        setForm((prev) => ({
            ...prev,
            logo: {
                type: nextType,
                value: ''
            }
        }));
    }

    function updateLogoUrl(value) {
        clearAiStateForPath('logo');
        setForm((prev) => ({
            ...prev,
            logo: {
                ...prev.logo,
                value
            }
        }));
    }

    async function updateLogoFile(file) {
        clearAiStateForPath('logo');
        if (!file) {
            setForm((prev) => ({
                ...prev,
                logo: {
                    ...prev.logo,
                    value: ''
                }
            }));
            return;
        }

        const value = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
            reader.onerror = () => reject(new Error('Kunde inte lasa logotypfilen.'));
            reader.readAsDataURL(file);
        });

        setForm((prev) => ({
            ...prev,
            logo: {
                ...prev.logo,
                value
            }
        }));
    }

    function removeImage(index) {
        clearAiStateForPath('images');
        setForm((prev) => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
    }

    function updateImageType(index, nextType) {
        clearAiStateForPath('images');
        setForm((prev) => ({
            ...prev,
            images: prev.images.map((image, i) =>
                i === index
                    ? {
                        type: nextType,
                        value: ''
                    }
                    : image
            )
        }));
    }

    function updateImageUrl(index, value) {
        clearAiStateForPath('images');
        setForm((prev) => ({
            ...prev,
            images: prev.images.map((image, i) => (i === index ? { ...image, value } : image))
        }));
    }

    async function updateImageFile(index, file) {
        clearAiStateForPath('images');
        if (!file) {
            setForm((prev) => ({
                ...prev,
                images: prev.images.map((image, i) => (i === index ? { ...image, value: '' } : image))
            }));
            return;
        }

        const value = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
            reader.onerror = () => reject(new Error('Kunde inte lasa bildfilen.'));
            reader.readAsDataURL(file);
        });

        setForm((prev) => ({
            ...prev,
            images: prev.images.map((image, i) => (i === index ? { ...image, value } : image))
        }));
    }

    function getAiBadge(path) {
        if (aiGeneratedFields[path]) {
            return strings.ai.badge;
        }

        if (aiSuggestions[path]) {
            return strings.ai.suggestionBadge;
        }

        return '';
    }

    function getAiSuggestion(path) {
        return aiSuggestions[path] ? formatAiSuggestion(aiSuggestions[path].value) : '';
    }

    function acceptAiSuggestion(path) {
        const suggestion = aiSuggestions[path];

        if (!suggestion) {
            return;
        }

        setForm((prev) => setPathValue(prev, path, suggestion.value));
        setAiGeneratedFields((prev) => ({
            ...prev,
            [path]: suggestion.meta || { source: 'AI' }
        }));
        setAiSuggestions((prev) => {
            const next = { ...prev };
            delete next[path];
            return next;
        });
    }

    function renderAiArraySuggestion(path) {
        const suggestion = aiSuggestions[path];

        if (!suggestion) {
            return null;
        }

        return (
            <div className="ai-suggestion-card">
                <span className="ai-badge">{strings.ai.suggestionBadge}</span>
                <p>{formatAiSuggestion(suggestion.value)}</p>
                <button type="button" className="button button--secondary" onClick={() => acceptAiSuggestion(path)}>
                    {strings.ai.acceptSuggestion}
                </button>
            </div>
        );
    }

    function applyResearchResult(data) {
        const values = data?.values || {};
        const fieldMeta = data?.fieldMeta || {};
        const candidatePaths = [
            'displayName',
            'organizationNumber',
            'contactPerson',
            'email',
            'phone',
            'city',
            'serviceArea',
            'industry',
            'businessDescription',
            'services',
            'usp',
            'yearsInBusiness',
            'certifications',
            'websiteEmail',
            'websitePhone',
            'address',
            'postalCode',
            'openingHours',
            'socialLinks.facebook',
            'socialLinks.instagram',
            'socialLinks.linkedin',
            'logo',
            'images'
        ];

        setForm((prev) => {
            let next = { ...prev };
            const nextGenerated = {};
            const nextSuggestions = {};

            for (const path of candidatePaths) {
                const aiValue = getPathValue(values, path);

                if (!isUsableAiValue(aiValue)) {
                    continue;
                }

                const currentValue = getPathValue(prev, path);
                const meta = getPathValue(fieldMeta, path) || fieldMeta[path] || { source: 'AI' };

                if (isEmptyValue(currentValue)) {
                    next = setPathValue(next, path, aiValue);
                    nextGenerated[path] = meta;
                } else if (formatAiSuggestion(currentValue) !== formatAiSuggestion(aiValue)) {
                    nextSuggestions[path] = {
                        value: aiValue,
                        meta
                    };
                }
            }

            setAiGeneratedFields(nextGenerated);
            setAiSuggestions(nextSuggestions);
            return next;
        });

        setAiResearch({
            loading: false,
            message: strings.ai.completed,
            summary: data?.summary || '',
            sources: Array.isArray(data?.sources) ? data.sources : []
        });
    }

    async function fetchAiResearch() {
        const companyName = form.companyName.trim();
        const websiteUrl = normalizeWebsiteUrl(form.currentWebsite);

        if (!companyName) {
            setAiResearch({
                loading: false,
                message: strings.ai.companyRequired,
                summary: '',
                sources: []
            });
            return;
        }

        setAiResearch({
            loading: true,
            message: strings.ai.loading,
            summary: '',
            sources: []
        });

        try {
            const response = await fetch(`${backendBaseUrl}/admin/create-site/research`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    companyName,
                    websiteUrl
                })
            });
            const result = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(result.error || strings.ai.failed);
            }

            applyResearchResult(result.data || {});
        } catch (error) {
            setAiResearch({
                loading: false,
                message: error.message || strings.ai.failed,
                summary: '',
                sources: []
            });
        }
    }

    function nextStep() {
        setStep((prev) => Math.min(prev + 1, totalSteps));
    }

    function previousStep() {
        setStep((prev) => Math.max(prev - 1, 1));
    }

    function goToStep(nextStep) {
        setStep(Math.min(Math.max(nextStep, 1), totalSteps));
    }

    useEffect(() => {
        if (!workflowTarget.siteId || !workflowTarget.commitSha || !accessToken) {
            return undefined;
        }

        let active = true;
        let timeoutId;

        async function loadWorkflowStatus() {
            if (active) {
                setWorkflowState((current) => ({ ...current, loading: true, error: '' }));
            }

            try {
                const response = await fetch(`${backendBaseUrl}/sites/${workflowTarget.siteId}/actions/runs?commitSha=${encodeURIComponent(workflowTarget.commitSha)}`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                });

                const result = await response.json().catch(() => ({}));

                if (!response.ok) {
                    throw new Error(result.error || strings.workflow.statusFetchError);
                }

                if (!active) {
                    return;
                }

                const latestRun = (result.data?.runs || [])[0] || null;
                const buildJob = latestRun ? findJobByKeyword(latestRun, 'build') : null;
                const deployJob = latestRun ? findJobByKeyword(latestRun, 'deploy') : null;
                const workflowCompleted = latestRun?.status === 'completed';
                const workflowSucceeded = workflowCompleted && latestRun?.conclusion === 'success';
                const buildStep = buildJob
                    ? getWorkflowStep(buildJob, strings.workflow.build)
                    : getRunDerivedStep(latestRun, strings.workflow.build);
                const deployStep = deployJob
                    ? getWorkflowStep(deployJob, strings.workflow.deploy)
                    : (latestRun ? getRunDerivedStep(latestRun, strings.workflow.deploy) : null);
                const deploySucceeded = deployJob
                    ? deployJob.status === 'completed' && deployJob.conclusion === 'success'
                    : workflowSucceeded;

                setWorkflowState({
                    loading: false,
                    error: '',
                    buildStep,
                    deployStep
                });

                const shouldKeepPolling = !latestRun || !workflowCompleted;

                if (deploySucceeded && pendingPagesUrl) {
                    setPagesStatus(strings.workflow.siteLive);
                    setPagesUrl(pendingPagesUrl);
                }

                if (shouldKeepPolling) {
                    timeoutId = window.setTimeout(loadWorkflowStatus, POLL_INTERVAL_MS);
                }
            } catch (error) {
                if (!active) {
                    return;
                }

                setWorkflowState({
                    loading: false,
                    error: error.message || strings.workflow.statusFetchError,
                    buildStep: null,
                    deployStep: null
                });

                timeoutId = window.setTimeout(loadWorkflowStatus, POLL_INTERVAL_MS);
            }
        }

        loadWorkflowStatus();

        return () => {
            active = false;
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [accessToken, pendingPagesUrl, workflowTarget]);

    function buildPayload(repoConflictStrategy) {
        const { currentWebsite, ...createSiteForm } = form;

        return {
            ...createSiteForm,
            services: form.services.filter(Boolean),
            usp: form.usp.filter(Boolean),
            certifications: form.certifications.filter(Boolean),
            openingHours: normalizeOpeningHours(form.openingHours),
            localFeel: form.localFeel ? 'high' : 'low',
            socialLinks: buildSocialLinksArray(form.socialLinks),
            logo: form.logo?.value ? form.logo : null,
            images: form.images.filter((image) => Boolean(image.value)),
            ...(repoConflictStrategy ? { repoConflictStrategy } : {})
        };
    }

    async function activatePages(siteId) {
        setPagesUrl('');
        setPagesStatus(strings.pages.activating);

        try {
            const response = await fetch(`${backendBaseUrl}/admin/sites/${siteId}/activate-pages`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            });

            const result = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(result.error || 'Kunde inte aktivera pages.');
            }

            setPagesStatus(strings.pages.enabledWaiting);
            return result.data?.pagesUrl || '';
        } catch (error) {
            const message = error.message || 'Kunde inte aktivera pages.';
            setPagesStatus(message);
            return '';
        }
    }

    async function submitCreateSite(repoConflictStrategy) {
        setSubmitting(true);
        setSubmitMessage('');
        setCreatedRepo(null);
        setPagesStatus('');
        setPagesUrl('');
        setRepoConflict(null);
        setWorkflowTarget({ siteId: '', commitSha: '' });
        setWorkflowState({ loading: false, error: '', buildStep: null, deployStep: null });
        setPendingPagesUrl('');
        setAiResearch({ loading: false, message: '', summary: '', sources: [] });
        setAiGeneratedFields({});
        setAiSuggestions({});

        try {
            const response = await fetch(`${backendBaseUrl}/admin/create-site`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify(buildPayload(repoConflictStrategy))
            });

            const result = await response.json().catch(() => ({}));

            if (!response.ok) {
                if (response.status === 409 && result?.details?.code === 'repo_exists') {
                    setRepoConflict(result.details);
                    setSubmitMessage(result.error || 'GitHub-repot finns redan.');
                    return;
                }

                throw new Error(result.error === strings.messages.validationFailed ? buildValidationMessage(result) : (result.error || strings.messages.createSiteFailed));
            }

            setCreatedRepo(result.data?.repo || null);
            setSubmitMessage(strings.messages.createdPrefix + (result.data?.repo?.name || 'klart'));
            if (result.data?.site?.site_id) {
                const nextPagesUrl = await activatePages(result.data.site.site_id);
                const resolvedPagesUrl = nextPagesUrl || buildFallbackPagesUrl(result.data?.repo);
                setPendingPagesUrl(resolvedPagesUrl);
                setWorkflowTarget({
                    siteId: result.data.site.site_id,
                    commitSha: result.data?.commitSha || ''
                });
            }
        } catch (error) {
            setSubmitMessage(error.message || strings.messages.genericError);
        } finally {
            setSubmitting(false);
        }
    }

    function clearAllFormData() {
        setForm({ currentWebsite: '', ...JSON.parse(JSON.stringify(emptyFormValues)), openingHours: createOpeningHoursDefaults() });
        setStep(1);
        setSubmitting(false);
        setSubmitMessage('');
        setCreatedRepo(null);
        setPagesStatus('');
        setPagesUrl('');
        setRepoConflict(null);
        setWorkflowTarget({ siteId: '', commitSha: '' });
        setWorkflowState({ loading: false, error: '', buildStep: null, deployStep: null });
        setPendingPagesUrl('');
        setAiResearch({ loading: false, message: '', summary: '', sources: [] });
        setAiGeneratedFields({});
        setAiSuggestions({});

        if (typeof window !== 'undefined') {
            window.localStorage.removeItem(FORM_STORAGE_KEY);
            window.localStorage.removeItem(STEP_STORAGE_KEY);
        }
    }

    function handleSubmit(e) {
        e.preventDefault();
    }

    return (
        <div className="admin-page">
            <div className="admin-layout">
                <aside className="admin-sidebar">
                    <div className="sidebar-header">
                        <p className="sidebar-kicker">{strings.sidebar.kicker}</p>
                        <h1 className="sidebar-title">{strings.sidebar.title}</h1>
                        <p className="sidebar-description">
                            {strings.sidebar.description}
                        </p>
                    </div>

                    <div className="step-list">
                        {Array.from({ length: totalSteps }).map((_, index) => {
                            const current = index + 1;
                            const isActive = current === step;
                            const isCompleted = current < step;

                            let cardClass = 'step-card';
                            if (isActive) cardClass += ' step-card--active';
                            if (isCompleted) cardClass += ' step-card--completed';

                            return (
                                <button
                                    key={current}
                                    type="button"
                                    className={cardClass}
                                    onClick={() => goToStep(current)}
                                    aria-current={isActive ? 'step' : undefined}
                                >
                                    <div className="step-card-row">
                                        <div className="step-card-number">{current}</div>
                                        <div>
                                            <p className="step-card-label">Steg {current}</p>
                                            <p className="step-card-title">{sectionTitle[current]}</p>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="sidebar-info-box">
                        <p className="sidebar-info-title">{strings.preflight.title}</p>
                        <ul className="sidebar-info-list">
                            {strings.preflight.items.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                    </div>
                </aside>

                <main className="admin-main">
                    <div className="main-header">
                        <div>
                            <p className="main-kicker">Steg {step} av {totalSteps}</p>
                            <h2 className="main-title">{sectionTitle[step]}</h2>
                        </div>

                        <div className="main-header__actions">
                            <button type="button" className="button button--secondary button--danger-lite" onClick={clearAllFormData} disabled={submitting}>{strings.buttons.clearAll}</button>
                            <div className="progress-wrapper">
                            <div className="progress-meta">
                                <span>{strings.progress}</span>
                                <span>{Math.round((step / totalSteps) * 100)}%</span>
                            </div>
                            <div className="progress-bar">
                                <div className="progress-bar-fill" style={{ width: `${(step / totalSteps) * 100}%` }} />
                            </div>
                            </div>
                        </div>
                    </div>

                    <form className="form-layout" onSubmit={handleSubmit} onKeyDown={(event) => { if (event.key === 'Enter' && step < totalSteps && event.target.tagName !== 'TEXTAREA') { event.preventDefault(); } }}>
                        {repoConflict ? (
                            <div className="warning-box">
                                <p className="warning-box__title">{strings.repoConflict.title}</p>
                                <p className="warning-box__text">{submitMessage}</p>
                                <p className="warning-box__text">
                                    {strings.repoConflict.existingRepo}{' '}
                                    {repoConflict.repo?.htmlUrl ? (
                                        <a href={repoConflict.repo.htmlUrl} target="_blank" rel="noreferrer" className="submit-message-link">
                                            {repoConflict.repo.name}
                                        </a>
                                    ) : (
                                        repoConflict.repo?.name
                                    )}
                                </p>
                                <div className="warning-box__actions">
                                    <button type="button" className="button button--secondary" disabled={submitting} onClick={() => submitCreateSite('overwrite')}>
                                        {strings.buttons.overwriteRepo}
                                    </button>
                                    <button type="button" className="button button--primary" disabled={submitting} onClick={() => submitCreateSite('copy')}>
                                        {strings.buttons.createCopy}
                                    </button>
                                </div>
                            </div>
                        ) : null}

                        {step === 1 && (
                            <section className="form-section">
                                <div className="research-card">
                                    <div className="research-card__header">
                                        <div>
                                            <p className="array-card-title">{strings.ai.title}</p>
                                            <p className="array-card-text">{strings.ai.description}</p>
                                        </div>
                                        <button type="button" className="button button--primary" onClick={fetchAiResearch} disabled={aiResearch.loading || submitting}>
                                            {aiResearch.loading ? strings.ai.loadingButton : strings.ai.button}
                                        </button>
                                    </div>
                                    <div className="form-grid form-grid--two">
                                        <Field label={strings.fields.currentWebsite}>
                                            <input className="form-input" value={form.currentWebsite || ''} placeholder="https://exempel.se" onChange={(e) => updateField('currentWebsite', e.target.value)} />
                                        </Field>
                                        <Field label={strings.fields.companyName} required>
                                            <input className="form-input" value={form.companyName} onChange={(e) => updateField('companyName', e.target.value)} />
                                        </Field>
                                    </div>
                                    {aiResearch.message ? (
                                        <p className={`research-card__message ${aiResearch.loading ? 'research-card__message--loading' : ''}`}>{aiResearch.message}</p>
                                    ) : null}
                                    {aiResearch.summary ? <p className="research-card__summary">{aiResearch.summary}</p> : null}
                                    {aiResearch.sources.length ? (
                                        <div className="research-card__sources">
                                            {aiResearch.sources.slice(0, 5).map((source) => (
                                                <a key={source} href={source} target="_blank" rel="noreferrer">{source}</a>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>

                                <div className="form-grid form-grid--two">
                                    <Field label={strings.fields.displayName} badge={getAiBadge('displayName')} suggestion={getAiSuggestion('displayName')} onAcceptSuggestion={() => acceptAiSuggestion('displayName')}>
                                        <input className="form-input" value={form.displayName} onChange={(e) => updateField('displayName', e.target.value)} />
                                    </Field>
                                    <Field label={strings.fields.organizationNumber} badge={getAiBadge('organizationNumber')} suggestion={getAiSuggestion('organizationNumber')} onAcceptSuggestion={() => acceptAiSuggestion('organizationNumber')}>
                                        <input className="form-input" value={form.organizationNumber || ''} placeholder="556000-0000" onChange={(e) => updateField('organizationNumber', e.target.value)} />
                                    </Field>
                                    <Field label={strings.fields.contactPerson} badge={getAiBadge('contactPerson')} suggestion={getAiSuggestion('contactPerson')} onAcceptSuggestion={() => acceptAiSuggestion('contactPerson')}>
                                        <input className="form-input" value={form.contactPerson} onChange={(e) => updateField('contactPerson', e.target.value)} />
                                    </Field>
                                    <Field label={strings.fields.email} badge={getAiBadge('email')} suggestion={getAiSuggestion('email')} onAcceptSuggestion={() => acceptAiSuggestion('email')}>
                                        <input type="email" className="form-input" value={form.email} onChange={(e) => updateField('email', e.target.value)} />
                                    </Field>
                                    <Field label={strings.fields.phone} badge={getAiBadge('phone')} suggestion={getAiSuggestion('phone')} onAcceptSuggestion={() => acceptAiSuggestion('phone')}>
                                        <input className="form-input" value={form.phone} onChange={(e) => updateField('phone', e.target.value)} />
                                    </Field>
                                    <Field label={strings.fields.city} badge={getAiBadge('city')} suggestion={getAiSuggestion('city')} onAcceptSuggestion={() => acceptAiSuggestion('city')}>
                                        <input className="form-input" value={form.city} onChange={(e) => updateField('city', e.target.value)} />
                                    </Field>
                                    <Field label={strings.fields.serviceArea} badge={getAiBadge('serviceArea')} suggestion={getAiSuggestion('serviceArea')} onAcceptSuggestion={() => acceptAiSuggestion('serviceArea')}>
                                        <input className="form-input" value={form.serviceArea} onChange={(e) => updateField('serviceArea', e.target.value)} />
                                    </Field>
                                    <Field label={strings.fields.industry} badge={getAiBadge('industry')} suggestion={getAiSuggestion('industry')} onAcceptSuggestion={() => acceptAiSuggestion('industry')}>
                                        <select className="form-input" value={form.industry} onChange={(e) => updateField('industry', e.target.value)}>
                                            {industries.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </Field>
                                </div>
                            </section>
                        )}

                        {step === 2 && (
                            <section className="form-section">
                                <Field label={strings.fields.businessDescription} badge={getAiBadge('businessDescription')} suggestion={getAiSuggestion('businessDescription')} onAcceptSuggestion={() => acceptAiSuggestion('businessDescription')}>
                                    <textarea className="form-textarea" value={form.businessDescription} onChange={(e) => updateField('businessDescription', e.target.value)} />
                                </Field>

                                <ArrayField label="Tjänster" values={form.services} onAdd={() => addArrayItem('services')} onRemove={(index) => removeArrayItem('services', index)} onChange={(index, value) => updateArrayItem('services', index, value)} placeholder="Exempel: Elinstallationer" />
                                {aiGeneratedFields.services ? <span className="ai-badge ai-badge--standalone">{strings.ai.badge}</span> : null}
                                {renderAiArraySuggestion('services')}

                                <div className="form-grid form-grid--two">
                                    <Field label={strings.fields.targetAudience}>
                                        <select className="form-input" value={form.targetAudience} onChange={(e) => updateField('targetAudience', e.target.value)}>
                                            <option value="private">Privatpersoner</option>
                                            <option value="business">Företag</option>
                                            <option value="both">Båda</option>
                                        </select>
                                    </Field>
                                    <Field label={strings.fields.yearsInBusiness} badge={getAiBadge('yearsInBusiness')} suggestion={getAiSuggestion('yearsInBusiness')} onAcceptSuggestion={() => acceptAiSuggestion('yearsInBusiness')}>
                                        <input type="number" className="form-input" value={form.yearsInBusiness} onChange={(e) => updateField('yearsInBusiness', e.target.value)} />
                                    </Field>
                                </div>

                                <ArrayField label="Unika styrkor / USP" values={form.usp} onAdd={() => addArrayItem('usp')} onRemove={(index) => removeArrayItem('usp', index)} onChange={(index, value) => updateArrayItem('usp', index, value)} placeholder="Exempel: Snabb återkoppling" />
                                {aiGeneratedFields.usp ? <span className="ai-badge ai-badge--standalone">{strings.ai.badge}</span> : null}
                                {renderAiArraySuggestion('usp')}

                                <ArrayField label={strings.fields.certifications} values={form.certifications} onAdd={() => addArrayItem('certifications')} onRemove={(index) => removeArrayItem('certifications', index)} onChange={(index, value) => updateArrayItem('certifications', index, value)} placeholder="Exempel: Auktoriserad elinstallator" />
                                {aiGeneratedFields.certifications ? <span className="ai-badge ai-badge--standalone">{strings.ai.badge}</span> : null}
                                {renderAiArraySuggestion('certifications')}
                            </section>
                        )}

                        {step === 3 && (
                            <section className="form-section">
                                <div className="form-grid form-grid--two">
                                    <Field label={strings.fields.language}>
                                        <select className="form-input" value={form.language} onChange={(e) => updateField('language', e.target.value)}>
                                            <option value="sv">Svenska</option>
                                            <option value="en">Engelska</option>
                                        </select>
                                    </Field>
                                    <Field label="Tone of voice">
                                        <select className="form-input" value={form.toneOfVoice} onChange={(e) => updateField('toneOfVoice', e.target.value)}>
                                            {toneOptions.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label={strings.fields.salesLevel}>
                                        <select className="form-input" value={form.salesLevel} onChange={(e) => updateField('salesLevel', e.target.value)}>
                                            <option value="low">Låg</option>
                                            <option value="medium">Medel</option>
                                            <option value="high">Hög</option>
                                        </select>
                                    </Field>
                                    <Field label={strings.fields.primaryCta}>
                                        <select className="form-input" value={form.primaryCta} onChange={(e) => updateField('primaryCta', e.target.value)}>
                                            {ctaOptions.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </Field>
                                </div>

                                <div className="toggle-panel">
                                    <div>
                                        <p className="toggle-panel-title">{strings.fields.localFeelTitle}</p>
                                        <p className="toggle-panel-text">{strings.fields.localFeelText}</p>
                                    </div>
                                    <button type="button" onClick={() => updateField('localFeel', !form.localFeel)} className={`switch ${form.localFeel ? 'switch--on' : ''}`}>
                                        <span className="switch-thumb" />
                                    </button>
                                </div>
                            </section>
                        )}

                        {step === 4 && (
                            <section className="form-section">
                                <div className="form-grid form-grid--two">
                                    <Field label={strings.fields.primaryColor}>
                                        <div className="color-input-row">
                                            <input type="color" value={form.primaryColor} onChange={(e) => updateField('primaryColor', e.target.value)} />
                                            <input className="color-text-input" value={form.primaryColor} onChange={(e) => updateField('primaryColor', e.target.value)} />
                                        </div>
                                    </Field>
                                    <Field label={strings.fields.secondaryColor}>
                                        <div className="color-input-row">
                                            <input type="color" value={form.secondaryColor} onChange={(e) => updateField('secondaryColor', e.target.value)} />
                                            <input className="color-text-input" value={form.secondaryColor} onChange={(e) => updateField('secondaryColor', e.target.value)} />
                                        </div>
                                    </Field>
                                    <Field label={strings.fields.theme}>
                                        <select className="form-input" value={form.theme} onChange={(e) => updateField('theme', e.target.value)}>
                                            {themeOptions.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label={strings.fields.themeMode}>
                                        <select className="form-input" value={form.visualStyle} onChange={(e) => updateField('visualStyle', e.target.value)}>
                                            <option value="light">Ljust</option>
                                            <option value="dark">Mörkt</option>
                                        </select>
                                    </Field>
                                </div>

                                <SingleImageInput
                                    title={getAiBadge('logo') ? `Logotyp (${getAiBadge('logo')})` : 'Logotyp'}
                                    description="Om du skickar med en logotyp visas den i headern. Om ingen logotyp skickas visas bara företagsnamnet."
                                    image={form.logo}
                                    onTypeChange={updateLogoType}
                                    onUrlChange={updateLogoUrl}
                                    onFileChange={updateLogoFile}
                                />
                                {renderAiArraySuggestion('logo')}

                                <div className="form-grid form-grid--two">
                                    <ToggleCard title={strings.toggles.testimonialsTitle} description={strings.toggles.testimonialsDescription} enabled={form.showTestimonials} onToggle={() => updateField('showTestimonials', !form.showTestimonials)} />
                                    <ToggleCard title={strings.toggles.faqTitle} description={strings.toggles.faqDescription} enabled={form.showFaq} onToggle={() => updateField('showFaq', !form.showFaq)} />
                                </div>
                            </section>
                        )}

                        {step === 5 && (
                            <section className="form-section">
                                <div className="opening-hours-card">
                                    <div className="array-card-header">
                                        <div>
                                            <p className="array-card-title">{strings.openingHours.title}</p>
                                            <p className="array-card-text">{strings.openingHours.description}</p>
                                        </div>
                                    </div>
                                    <div className="opening-hours-card__rows">
                                        {normalizeOpeningHours(form.openingHours).map((item, index) => (
                                            <div key={item.day} className={`opening-hours-row ${item.closed ? 'opening-hours-row--closed' : ''}`}>
                                                <div className="opening-hours-row__day">{item.day}</div>
                                                <label className="opening-hours-row__field">
                                                    <span>{strings.openingHours.opens}</span>
                                                    <input
                                                        className="form-input"
                                                        value={item.opens}
                                                        placeholder="10.00"
                                                        disabled={item.closed}
                                                        onChange={(event) => updateOpeningHour(index, 'opens', event.target.value)}
                                                    />
                                                </label>
                                                <label className="opening-hours-row__field">
                                                    <span>{strings.openingHours.closes}</span>
                                                    <input
                                                        className="form-input"
                                                        value={item.closes}
                                                        placeholder="13.00"
                                                        disabled={item.closed}
                                                        onChange={(event) => updateOpeningHour(index, 'closes', event.target.value)}
                                                    />
                                                </label>
                                                <label className="opening-hours-row__toggle">
                                                    <span>{strings.openingHours.closed}</span>
                                                    <button
                                                        type="button"
                                                        className={`switch ${item.closed ? 'switch--on' : ''}`}
                                                        aria-pressed={item.closed}
                                                        onClick={() => updateOpeningHour(index, 'closed', !item.closed)}
                                                    >
                                                        <span className="switch-thumb" />
                                                    </button>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="form-grid form-grid--two">
                                    <Field label={strings.fields.websiteEmail} badge={getAiBadge('websiteEmail')} suggestion={getAiSuggestion('websiteEmail')} onAcceptSuggestion={() => acceptAiSuggestion('websiteEmail')}>
                                        <input type="email" className="form-input" value={form.websiteEmail} onChange={(e) => updateField('websiteEmail', e.target.value)} />
                                    </Field>
                                    <Field label={strings.fields.websitePhone} badge={getAiBadge('websitePhone')} suggestion={getAiSuggestion('websitePhone')} onAcceptSuggestion={() => acceptAiSuggestion('websitePhone')}>
                                        <input className="form-input" value={form.websitePhone} onChange={(e) => updateField('websitePhone', e.target.value)} />
                                    </Field>
                                    <Field label={strings.fields.address} badge={getAiBadge('address')} suggestion={getAiSuggestion('address')} onAcceptSuggestion={() => acceptAiSuggestion('address')}>
                                        <input className="form-input" value={form.address} onChange={(e) => updateField('address', e.target.value)} />
                                    </Field>
                                    <Field label={strings.fields.postalCode} badge={getAiBadge('postalCode')} suggestion={getAiSuggestion('postalCode')} onAcceptSuggestion={() => acceptAiSuggestion('postalCode')}>
                                        <input className="form-input" value={form.postalCode} onChange={(e) => updateField('postalCode', e.target.value)} />
                                    </Field>
                                </div>

                                <div className="form-grid form-grid--three">
                                    <Field label={strings.fields.facebook} badge={getAiBadge('socialLinks.facebook')} suggestion={getAiSuggestion('socialLinks.facebook')} onAcceptSuggestion={() => acceptAiSuggestion('socialLinks.facebook')}>
                                        <input className="form-input" value={form.socialLinks.facebook} onChange={(e) => updateNestedField('socialLinks', 'facebook', e.target.value)} />
                                    </Field>
                                    <Field label={strings.fields.instagram} badge={getAiBadge('socialLinks.instagram')} suggestion={getAiSuggestion('socialLinks.instagram')} onAcceptSuggestion={() => acceptAiSuggestion('socialLinks.instagram')}>
                                        <input className="form-input" value={form.socialLinks.instagram} onChange={(e) => updateNestedField('socialLinks', 'instagram', e.target.value)} />
                                    </Field>
                                    <Field label={strings.fields.linkedin} badge={getAiBadge('socialLinks.linkedin')} suggestion={getAiSuggestion('socialLinks.linkedin')} onAcceptSuggestion={() => acceptAiSuggestion('socialLinks.linkedin')}>
                                        <input className="form-input" value={form.socialLinks.linkedin} onChange={(e) => updateNestedField('socialLinks', 'linkedin', e.target.value)} />
                                    </Field>
                                </div>

                                <ImageInputList
                                    images={form.images}
                                    onAdd={addImage}
                                    onRemove={removeImage}
                                    onTypeChange={updateImageType}
                                    onUrlChange={updateImageUrl}
                                    onFileChange={updateImageFile}
                                />
                                {aiGeneratedFields.images ? <span className="ai-badge ai-badge--standalone">{strings.ai.badge}</span> : null}
                                {renderAiArraySuggestion('images')}

                                <div className="summary-box">
                                    <p className="summary-box-title">{strings.summary.title}</p>
                                    <div className="summary-grid">
                                        <SummaryItem label={strings.summary.company} value={form.companyName || '-'} />
                                        <SummaryItem label={strings.summary.industry} value={industries.find((item) => item.value === form.industry)?.label || '-'} />
                                        <SummaryItem label={strings.summary.language} value={form.language === 'sv' ? 'Svenska' : 'Engelska'} />
                                        <SummaryItem label={strings.summary.primaryCta} value={ctaOptions.find((item) => item.value === form.primaryCta)?.label || '-'} />
                                    </div>
                                </div>
                            </section>
                        )}

                        <div className="form-actions">
                            <div className="submit-message">
                                {submitMessage}
                                {createdRepo?.htmlUrl ? (
                                    <>
                                        {' '}
                                        <a href={createdRepo.htmlUrl} target="_blank" rel="noreferrer" className="submit-message-link">{createdRepo.name}</a>
                                    </>
                                ) : null}
                                {pagesStatus ? (
                                    <div className="pages-status-row">
                                        <span>{pagesStatus}</span>
                                        {pagesUrl ? (
                                            <>
                                                {' '}
                                                <a href={pagesUrl} target="_blank" rel="noreferrer" className="submit-message-link">{pagesUrl}</a>
                                            </>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>

                            {(workflowState.buildStep || workflowState.error || workflowTarget.commitSha) ? (
                                <div className="workflow-status-card">
                                    <p className="workflow-status-card__title">{strings.workflow.title}</p>
                                    {workflowState.error ? <p className="workflow-status-card__error">{workflowState.error}</p> : null}
                                    {!workflowState.error && !workflowState.buildStep ? (
                                        <div className="workflow-status-step workflow-status-step--waiting">{strings.workflow.build + ' ' + strings.workflow.waiting}</div>
                                    ) : null}
                                    {workflowState.buildStep ? (
                                        <div className={`workflow-status-step workflow-status-step--${workflowState.buildStep.tone}`}>
                                            <span>{workflowState.buildStep.label}</span>
                                            {workflowState.buildStep.time ? <span className="workflow-status-step__time">{workflowState.buildStep.time}</span> : null}
                                        </div>
                                    ) : null}
                                    {workflowState.deployStep ? (
                                        <div className={`workflow-status-step workflow-status-step--${workflowState.deployStep.tone}`}>
                                            <span>{workflowState.deployStep.label}</span>
                                            {workflowState.deployStep.time ? <span className="workflow-status-step__time">{workflowState.deployStep.time}</span> : null}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}

                            <div className="button-group">
                                <button type="button" onClick={previousStep} disabled={step === 1} className="button button--secondary">{strings.buttons.previous}</button>
                                {step < totalSteps ? (
                                    <button type="button" onClick={nextStep} className="button button--primary">{strings.buttons.nextStep}</button>
                                ) : (
                                    <button type="button" onClick={() => submitCreateSite()} disabled={submitting} className="button button--primary">{submitting ? strings.buttons.creatingSite : strings.buttons.createSite}</button>
                                )}
                            </div>
                        </div>
                    </form>
                </main>
            </div>
        </div>
    );
}


